import { FileImage } from "lucide-react";
import { useState, useEffect } from "react";
import heartLoading from "../../../../assests/heartLoading.gif";

const UploadImage = ({
  diagrams,
  onFileSelect,
  onRemove,
  onDescriptionChange,
  onSubmit,
  fileInputRef,
  loadingExisting,  
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const activeDiagrams = diagrams.filter((d) => !d.isRemoved);
  const current = activeDiagrams[activeIndex];

  const hadExistingImages = diagrams.some((d) => d.isExisting);
  const allRemoved = activeDiagrams.length === 0 && diagrams.length > 0;
  const showEmptySubmit = allRemoved && hadExistingImages;

  useEffect(() => {
    setActiveIndex(0);
  }, [activeDiagrams.length]);

  const handleNext = () => {
    if (!current?.description?.trim()) return;

    if (activeIndex === activeDiagrams.length - 1) {
      onSubmit();
      return;
    }

    setActiveIndex((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setActiveIndex((i) => Math.max(0, i - 1));
  };

  const handleRemoveCurrent = () => {
    onRemove(current.id);
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    onFileSelect(files);

    setTimeout(() => setIsUploading(false), files.length * 300 + 500);
  };

  if (loadingExisting) {
    return (
      <div className="flex flex-col items-center justify-center h-[320px] gap-3 text-gray-400">
        <img src={heartLoading} alt="Loading..." className="w-[80px] h-[80px]" />
        <p className="text-sm font-medium">Loading existing diagrams...</p>
      </div>
    );
  }

  return (
    <>
      <div
        onClick={() => fileInputRef.current.click()}
        className="border-dashed border-2 border-indigo-400 rounded-xl p-12 text-center cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition-colors"
      >
        <FileImage size={48} className="mx-auto text-indigo-500 mb-3" />
        <p className="font-semibold text-black">
          Click or Drag & Drop ER Diagrams
        </p>
        <p className="text-sm text-gray-500">
          Max 5MB • PNG / JPG / GIF / WEBP / SVG
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        hidden
        onChange={handleFileChange}
      />

      {isUploading && (
        <div className="flex items-center justify-center gap-2 mt-4 text-indigo-600">
          <img src={heartLoading} alt="Uploading..." className="w-[80px] h-[80px]" />
          <p className="text-sm font-medium">Uploading images, please wait...</p>
        </div>
      )}

      {!isUploading && diagrams.length === 0 && (
        <div className="flex flex-col items-center justify-center mt-8 gap-2 text-gray-400">
          <FileImage size={40} className="text-gray-300" />
          <p className="text-sm font-medium">No diagrams added yet.</p>
          <p className="text-xs text-gray-400">
            Use the area above to upload your ER diagrams.
          </p>
        </div>
      )}

      {!isUploading && allRemoved && (
        <div className="flex flex-col items-center justify-center mt-8 gap-3">
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <FileImage size={40} className="text-gray-300" />
            {showEmptySubmit ? (
              <>
                <p className="text-sm font-medium text-gray-500">
                  All diagrams have been removed.
                </p>
                <p className="text-xs text-gray-400">
                  Click Submit to save this change, or add new diagrams above.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-500">
                  No diagrams to display.
                </p>
                <p className="text-xs text-gray-400">
                  Add new diagrams using the area above.
                </p>
              </>
            )}
          </div>

          {showEmptySubmit && (
            <button
              onClick={onSubmit}
              className="mt-2 bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-md text-sm transition-colors"
            >
              Submit
            </button>
          )}
        </div>
      )}

      {!isUploading && activeDiagrams.length > 0 && current && (
        <div className="flex items-center justify-center">
          <div className="mt-6 border rounded-lg w-[80%] p-4 bg-indigo-50">
            <div className="flex flex-col gap-1 mb-3">
              <h3 className="text-md font-semibold text-black">
                Add Description for Diagram {activeIndex + 1} of{" "}
                {activeDiagrams.length}
              </h3>
              <p className="text-sm text-gray-500">
                Provide a description for this ER diagram to identify it later.
              </p>
            </div>

            <div className="w-full h-[260px] flex items-center justify-center bg-white rounded-md mb-4">
              <img
                src={current.preview}
                alt={current.name}
                className="max-h-full max-w-full object-contain"
              />
            </div>

            <label className="block font-medium mb-2 text-black">
              Description (Mandatory):
            </label>
            <textarea
              value={current.description}
              onChange={(e) => onDescriptionChange(current.id, e.target.value)}
              placeholder="e.g. User authentication and authorization schema"
              className="w-full border rounded-md p-3 text-sm resize-none text-black"
              rows={1}
            />

            <div className="flex justify-between items-center mt-4">
              <div>
                {activeIndex > 0 && (
                  <button
                    onClick={handlePrevious}
                    className="bg-[#FF03F3] text-white px-4 py-2 border rounded-md text-sm"
                  >
                    Previous
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleRemoveCurrent}
                  className="bg-red-100 text-red-600 px-4 py-2 rounded-md"
                >
                  Remove
                </button>

                <button
                  onClick={handleNext}
                  disabled={!current.description.trim()}
                  className={`${
                    activeIndex === activeDiagrams.length - 1
                      ? "bg-teal-500 hover:bg-teal-600"
                      : "bg-[#FF03F3] hover:bg-[#00008b]"
                  } text-white px-4 py-2 rounded-md disabled:opacity-40 transition-colors`}
                >
                  {activeIndex === activeDiagrams.length - 1
                    ? "Submit"
                    : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadImage;